import React, { useState, useEffect } from 'react';
import './token-activity.css';

const TokenActivity = ({ userId, showAll = false }) => {
  const [transactions, setTransactions] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [balance, setBalance] = useState(0);
  const [platformAccounts, setPlatformAccounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState({
    institutional_mode: false,
    token_display_name: "Tokens",
    show_token_balance_in_lms: true
  });

  // Phase 3.5: Flow state and multiplier tracking
  const [flowStateCount, setFlowStateCount] = useState(0);
  const [totalMultiplierBonus, setTotalMultiplierBonus] = useState(0);
  const [bloomDistribution, setBloomDistribution] = useState(null);
  const [weeklyProgress, setWeeklyProgress] = useState([]);

  useEffect(() => {
    const fetchTokenData = async () => {
      setLoading(true);
      try {
        // Fetch token economy settings first
        const settingsResponse = await fetch('/api/token/settings');
        const settingsData = await settingsResponse.json();
        setSettings(settingsData || {
          institutional_mode: false,
          token_display_name: "Tokens",
          show_token_balance_in_lms: true
        });
        
        // Fetch user balance
        const balanceResponse = await fetch(`/api/token/balance?user_id=${userId}`);
        const balanceData = await balanceResponse.json();
        
        // Fetch transaction history
        const historyResponse = await fetch(`/api/token/history?user_id=${userId}&limit=${showAll ? 50 : 10}`);
        const historyData = await historyResponse.json();
        
        // Fetch achievements
        const achievementsResponse = await fetch(`/api/token/achievements?user_id=${userId}`);
        const achievementsData = await achievementsResponse.json();
        
        // Set state with fetched data
        setBalance(balanceData.balance);
        setTransactions(historyData.transactions || []);
        setAchievements(achievementsData.achievements || []);

        // Phase 3.5: Calculate flow state and multiplier metrics
        const flowTransactions = (historyData.transactions || []).filter(t =>
          t.transaction_metadata?.flow_bonus || t.transaction_metadata?.cognitive_state?.flow_state
        );
        setFlowStateCount(flowTransactions.length);

        // Calculate total multiplier bonus tokens earned
        const bonusTokens = flowTransactions.reduce((sum, t) => {
          const base = t.transaction_metadata?.base_tokens || (t.amount / 3);
          return sum + (t.amount - base);
        }, 0);
        setTotalMultiplierBonus(Math.round(bonusTokens));

        // Generate weekly progress chart data (last 7 days)
        const last7Days = Array.from({length: 7}, (_, i) => {
          const date = new Date();
          date.setDate(date.getDate() - (6 - i));
          return {
            day: date.toLocaleDateString('en-US', { weekday: 'short' }),
            tokens: 0,
            flowBonus: 0
          };
        });

        (historyData.transactions || []).forEach(t => {
          const tDate = new Date(t.timestamp);
          const dayIndex = last7Days.findIndex(d => {
            const checkDate = new Date();
            checkDate.setDate(checkDate.getDate() - (6 - last7Days.indexOf(d)));
            return tDate.toDateString() === checkDate.toDateString();
          });
          if (dayIndex >= 0) {
            last7Days[dayIndex].tokens += t.amount;
            if (t.transaction_metadata?.flow_bonus) {
              last7Days[dayIndex].flowBonus += t.amount;
            }
          }
        });
        setWeeklyProgress(last7Days);

        // In a real implementation, we'd also fetch platform accounts
        setPlatformAccounts({
          "discord": "discord_user_123",
          "canvas": "canvas_student_456"
        });

        setLoading(false);
      } catch (err) {
        console.error("Error fetching token data:", err);
        setError("Failed to load token data. Please try again.");
        setLoading(false);
        
        // Set demo data for UI testing
        setBalance(120);
        setTransactions([
          {
            id: "tx1",
            sender: "system",
            receiver: userId,
            amount: 15,
            platform: "lms",
            description: "Completed algebra practice",
            timestamp: "2025-03-24T14:30:00Z"
          },
          {
            id: "tx2",
            sender: "system",
            receiver: userId,
            amount: 30,
            platform: "discord",
            description: "Weekly Warrior achievement",
            timestamp: "2025-03-23T10:15:00Z",
            achievement_id: "streak_7_days"
          },
          {
            id: "tx3",
            sender: userId,
            receiver: "student_456",
            amount: 10,
            platform: "web",
            description: "Thank you for the help!",
            timestamp: "2025-03-22T16:45:00Z"
          }
        ]);
        setAchievements([
          {
            id: "streak_3_days",
            title: "3-Day Streak",
            description: "Used the tutor for 3 days in a row",
            token_reward: 15,
            icon: "streak_3.png"
          },
          {
            id: "streak_7_days",
            title: "Weekly Warrior",
            description: "Used the tutor for 7 days in a row",
            token_reward: 30,
            icon: "streak_7.png"
          }
        ]);
        setPlatformAccounts({
          "discord": "discord_user_123",
          "canvas": "canvas_student_456"
        });
      }
    };
    
    if (userId) {
      fetchTokenData();
    }
  }, [userId, showAll]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  const getPlatformIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'discord':
        return 'discord-icon';
      case 'canvas':
      case 'lms':
        return 'lms-icon';
      case 'web':
        return 'web-icon';
      default:
        return 'system-icon';
    }
  };
  
  if (loading) {
    return <div className="token-activity-loading">Loading token activity...</div>;
  }
  
  if (error) {
    return <div className="token-activity-error">{error}</div>;
  }

  return (
    <div style={{
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderRadius: '24px',
      padding: '32px',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, sans-serif",
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      {/* Hero Section - Balance & Stats */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1), rgba(16, 185, 129, 0.1))',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        borderRadius: '20px',
        padding: '32px',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.1) inset'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '24px'
        }}>
          {/* Total Balance */}
          <div>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#64748b',
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Total Balance
            </div>
            <div style={{
              fontSize: '48px',
              fontWeight: '700',
              color: '#0ea5e9',
              lineHeight: '1',
              fontVariantNumeric: 'tabular-nums',
              marginBottom: '4px'
            }}>
              {balance.toLocaleString()}
            </div>
            <div style={{
              fontSize: '13px',
              color: '#94a3b8'
            }}>
              {settings.institutional_mode ? 'Progress Points' : settings.token_display_name}
            </div>
          </div>

          {/* Flow State Stats */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(249, 115, 22, 0.1))',
            border: '1px solid rgba(245, 158, 11, 0.2)',
            borderRadius: '16px',
            padding: '20px'
          }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#f59e0b',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span>🔥</span>
              FLOW STATE SESSIONS
            </div>
            <div style={{
              fontSize: '36px',
              fontWeight: '700',
              color: '#f59e0b',
              lineHeight: '1',
              marginBottom: '4px'
            }}>
              {flowStateCount}
            </div>
            <div style={{
              fontSize: '12px',
              color: '#d97706'
            }}>
              +{totalMultiplierBonus} bonus tokens earned
            </div>
          </div>

          {/* Weekly Activity */}
          <div>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#64748b',
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              This Week
            </div>
            <div style={{
              fontSize: '36px',
              fontWeight: '700',
              color: '#10b981',
              lineHeight: '1',
              marginBottom: '4px'
            }}>
              {weeklyProgress.reduce((sum, day) => sum + day.tokens, 0)}
            </div>
            <div style={{
              fontSize: '12px',
              color: '#059669'
            }}>
              tokens earned
            </div>
          </div>
        </div>

        {/* Weekly Progress Chart */}
        <div style={{
          marginTop: '32px',
          paddingTop: '24px',
          borderTop: '1px solid rgba(148, 163, 184, 0.2)'
        }}>
          <div style={{
            fontSize: '13px',
            fontWeight: '600',
            color: '#64748b',
            marginBottom: '16px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            7-Day Progress
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '8px',
            height: '120px'
          }}>
            {weeklyProgress.map((day, i) => {
              const maxTokens = Math.max(...weeklyProgress.map(d => d.tokens), 1);
              const height = (day.tokens / maxTokens) * 100;
              const hasFlow = day.flowBonus > 0;

              return (
                <div key={i} style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{
                    width: '100%',
                    background: hasFlow
                      ? 'linear-gradient(180deg, #f59e0b 0%, #f97316 100%)'
                      : 'linear-gradient(180deg, #0ea5e9 0%, #10b981 100%)',
                    borderRadius: '8px 8px 0 0',
                    height: `${height}%`,
                    minHeight: day.tokens > 0 ? '4px' : '0',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    boxShadow: hasFlow ? '0 4px 12px rgba(245, 158, 11, 0.3)' : '0 2px 8px rgba(14, 165, 233, 0.2)'
                  }}>
                    {hasFlow && (
                      <div style={{
                        position: 'absolute',
                        top: '-6px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontSize: '12px'
                      }}>
                        🔥
                      </div>
                    )}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#64748b'
                  }}>
                    {day.day}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '700',
                    color: hasFlow ? '#f59e0b' : '#0ea5e9'
                  }}>
                    {day.tokens}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Modern Transaction List */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: showAll ? '1fr' : 'repeat(auto-fit, minmax(500px, 1fr))',
        gap: '24px'
      }}>
        {/* Transaction History */}
        <div>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#0f172a',
            marginBottom: '20px'
          }}>
            Recent Activity
          </h3>
          {transactions.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '48px 20px',
              background: 'rgba(255, 255, 255, 0.5)',
              borderRadius: '16px',
              border: '1px dashed #cbd5e1'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🪙</div>
              <div style={{ color: '#64748b', fontSize: '14px' }}>
                No transactions yet. Start learning to earn tokens!
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {transactions.map(transaction => {
                const isFlowBonus = transaction.transaction_metadata?.flow_bonus;
                const multiplier = transaction.transaction_metadata?.multipliers?.[0]?.multiplier || 1.0;
                const bloomLevel = transaction.transaction_metadata?.bloom_level;

                return (
                  <div key={transaction.id} style={{
                    background: isFlowBonus
                      ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.05), rgba(249, 115, 22, 0.05))'
                      : 'rgba(255, 255, 255, 0.7)',
                    backdropFilter: 'blur(10px)',
                    border: isFlowBonus
                      ? '1px solid rgba(245, 158, 11, 0.2)'
                      : '1px solid rgba(226, 232, 240, 0.8)',
                    borderRadius: '16px',
                    padding: '16px',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    ':hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
                    }
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px'
                    }}>
                      {/* Icon */}
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '12px',
                        background: isFlowBonus
                          ? 'linear-gradient(135deg, #f59e0b, #f97316)'
                          : transaction.sender === userId
                            ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                            : 'linear-gradient(135deg, #10b981, #059669)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        color: 'white',
                        flexShrink: 0,
                        boxShadow: isFlowBonus
                          ? '0 4px 12px rgba(245, 158, 11, 0.3)'
                          : '0 2px 8px rgba(0, 0, 0, 0.1)'
                      }}>
                        {isFlowBonus ? '🔥' : transaction.sender === userId ? '↑' : '↓'}
                      </div>

                      {/* Details */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: '15px',
                          fontWeight: '600',
                          color: '#0f172a',
                          marginBottom: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          flexWrap: 'wrap'
                        }}>
                          <span>{transaction.description}</span>

                          {/* Multiplier Badge */}
                          {multiplier > 1.0 && (
                            <span style={{
                              background: 'linear-gradient(135deg, #f59e0b, #f97316)',
                              color: 'white',
                              fontSize: '10px',
                              fontWeight: '700',
                              padding: '2px 6px',
                              borderRadius: '6px',
                              letterSpacing: '0.5px'
                            }}>
                              {multiplier.toFixed(1)}×
                            </span>
                          )}

                          {/* Bloom Level Badge */}
                          {bloomLevel && (
                            <span style={{
                              background: '#8b5cf620',
                              border: '1px solid #8b5cf640',
                              color: '#8b5cf6',
                              fontSize: '10px',
                              fontWeight: '600',
                              padding: '2px 6px',
                              borderRadius: '6px'
                            }}>
                              {bloomLevel.toUpperCase()}
                            </span>
                          )}

                          {transaction.achievement_id && (
                            <span style={{
                              background: '#fbbf2420',
                              border: '1px solid #fbbf2440',
                              color: '#d97706',
                              fontSize: '10px',
                              fontWeight: '600',
                              padding: '2px 6px',
                              borderRadius: '6px'
                            }}>
                              ACHIEVEMENT
                            </span>
                          )}
                        </div>

                        <div style={{
                          fontSize: '12px',
                          color: '#64748b',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <span>{formatDate(transaction.timestamp)}</span>
                          {transaction.platform && (
                            <>
                              <span>•</span>
                              <span style={{ textTransform: 'capitalize' }}>{transaction.platform}</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Amount */}
                      <div style={{
                        fontSize: '20px',
                        fontWeight: '700',
                        color: isFlowBonus
                          ? '#f59e0b'
                          : transaction.sender === userId
                            ? '#ef4444'
                            : '#10b981',
                        textAlign: 'right',
                        flexShrink: 0,
                        fontVariantNumeric: 'tabular-nums'
                      }}>
                        {transaction.sender === userId ? '-' : '+'}{transaction.amount}
                        <div style={{
                          fontSize: '11px',
                          fontWeight: '500',
                          color: '#94a3b8',
                          marginTop: '2px'
                        }}>
                          {settings.institutional_mode ? 'pts' : 'tokens'}
                        </div>
                      </div>
                    </div>

                    {/* Flow State Breakdown */}
                    {isFlowBonus && transaction.transaction_metadata?.breakdown && (
                      <div style={{
                        marginTop: '12px',
                        paddingTop: '12px',
                        borderTop: '1px solid rgba(245, 158, 11, 0.2)',
                        fontSize: '12px',
                        color: '#d97706',
                        fontWeight: '500'
                      }}>
                        💡 {transaction.transaction_metadata.breakdown}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          
          {!showAll && transactions.length > 0 && (
            <button style={{
              width: '100%',
              padding: '12px',
              marginTop: '16px',
              background: 'rgba(14, 165, 233, 0.1)',
              border: '1px solid rgba(14, 165, 233, 0.3)',
              borderRadius: '12px',
              color: '#0ea5e9',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(14, 165, 233, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(14, 165, 233, 0.1)';
            }}
            >
              View All Transactions →
            </button>
          )}
        </div>

        {/* Modern Achievements Section */}
        {!showAll && (
          <div>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '700',
              color: '#0f172a',
              marginBottom: '20px'
            }}>
              Achievements
            </h3>
            {achievements.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '48px 20px',
                background: 'rgba(255, 255, 255, 0.5)',
                borderRadius: '16px',
                border: '1px dashed #cbd5e1'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏆</div>
                <div style={{ color: '#64748b', fontSize: '14px' }}>
                  Complete challenges to unlock achievements!
                </div>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: '16px'
              }}>
                {achievements.map(achievement => (
                  <div key={achievement.id} style={{
                    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05), rgba(99, 102, 241, 0.05))',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(139, 92, 246, 0.2)',
                    borderRadius: '16px',
                    padding: '20px',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 32px rgba(139, 92, 246, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  >
                    <div style={{
                      fontSize: '40px',
                      marginBottom: '12px',
                      textAlign: 'center'
                    }}>
                      {achievement.icon || '🏆'}
                    </div>
                    <div style={{
                      fontSize: '15px',
                      fontWeight: '600',
                      color: '#0f172a',
                      marginBottom: '6px',
                      textAlign: 'center'
                    }}>
                      {achievement.title}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#64748b',
                      marginBottom: '12px',
                      textAlign: 'center',
                      lineHeight: '1.4'
                    }}>
                      {achievement.description}
                    </div>
                    <div style={{
                      background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                      color: 'white',
                      padding: '8px 12px',
                      borderRadius: '10px',
                      fontSize: '13px',
                      fontWeight: '700',
                      textAlign: 'center'
                    }}>
                      +{achievement.token_reward} {settings.institutional_mode ? 'pts' : 'tokens'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TokenActivity;